import axios from 'axios'
import { defineStore } from 'pinia'
import type { DocumentTreeItem } from '../types/tree.types'
import { getApiTreeChildren } from '../utils/normalizeApiTree'
import { BASE_URL } from '@/environment/environment'
import type { IUniversalTreeSelectModule } from '@/types/IUniversalTreeSelectModule'

const API_BASE = `${BASE_URL}/api/documents`
const FILTER_CHOICES_URL = `${API_BASE}/document/filter-choices/`

const ALLOWED_FIELDS = ['authors', 'categories', 'scopes', 'status', 'sections', 'types'] as const

function toChoice(item: { id: number | string; name?: string; label?: string; children?: unknown[]; childrens?: unknown[] }): DocumentTreeItem {
  const nodes = getApiTreeChildren(item)
  return {
    id: String(item.id),
    label: item.label ?? item.name ?? String(item.id),
    children: nodes.length ? nodes.map(n => toChoice(n as typeof item)) : [],
  }
}

function flattenSectionChoices(items: DocumentTreeItem[]): { title: string; value: string }[] {
  const result: { title: string; value: string }[] = []
  function walk(nodes: DocumentTreeItem[], prefix = '') {
    for (const n of nodes) {
      result.push({ title: prefix + (n.label || String(n.id)), value: String(n.id) })
      if (n.children?.length) walk(n.children, `${prefix}  `)
    }
  }
  walk(items)
  return result
}

export type DocumentsFilterSource = 'local' | 'global'

export const useDocumentsFilterStore = defineStore('documentsFilter', {
  state: () => ({
    /** local — портал; global — виды/значения из daomed_main */
    source: 'local' as DocumentsFilterSource,
    filterChoices: {} as Record<string, DocumentTreeItem[]>,
    searchChoices: {} as Record<string, DocumentTreeItem[]>,
    filterCursors: {} as Record<string, string | null>,
    filterLoading: {} as Record<string, boolean>,
    filterStrategies: {} as Record<string, string>,
    filters: {
      types: [] as string[],
      scopes: [] as string[],
      categories: [] as string[],
      status: [] as string[],
      sections: [] as string[],
      authors: [] as string[],
    },
  }),

  getters: {
    sectionSelectOptions(state): { title: string; value: string }[] {
      return flattenSectionChoices(state.filterChoices.sections || [])
    },
  },

  actions: {
    setFilterSource(source: DocumentsFilterSource) {
      if (this.source === source) {
        return
      }
      this.source = source
      this.filterChoices = {}
      this.searchChoices = {}
      this.filterCursors = {}
    },

    async fetchFilterChoices(
      fieldKey: string,
      search = '',
      cursor?: string,
      _module?: IUniversalTreeSelectModule,
    ): Promise<void> {
      if (!fieldKey || !ALLOWED_FIELDS.includes(fieldKey as typeof ALLOWED_FIELDS[number])) {
        return
      }

      this.filterLoading[fieldKey] = true
      try {
        const params: Record<string, string> = {
          field: fieldKey,
          source: this.source,
        }
        if (search) {
          params.search_field = search
        }
        if (cursor) {
          params.cursor = cursor
        }

        if (fieldKey !== 'types' && this.filters.types?.length) {
          params.types = this.filters.types.join(',')
        }
        if (fieldKey !== 'scopes' && this.filters.scopes?.length) {
          params.scopes = this.filters.scopes.join(',')
        }
        if (fieldKey !== 'categories' && this.filters.categories?.length) {
          params.categories = this.filters.categories.join(',')
        }
        if (fieldKey !== 'status' && this.filters.status?.length) {
          params.status = this.filters.status.join(',')
        }
        if (fieldKey !== 'sections' && this.filters.sections?.length) {
          params.sections = this.filters.sections.join(',')
        }
        if (fieldKey !== 'authors' && this.filters.authors?.length) {
          params.authors = this.filters.authors.join(',')
        }

        const { data } = await axios.get<{ results?: unknown[]; next?: string | null; strategy?: string }>(
          FILTER_CHOICES_URL,
          { params },
        )

        const results: DocumentTreeItem[] = (data.results || []).map((r: any) => toChoice(r))

        this.filterStrategies[fieldKey] = data.strategy || 'independent'

        if (search) {
          this.searchChoices[fieldKey] = cursor
            ? [...(this.searchChoices[fieldKey] || []), ...results]
            : results
        } else {
          this.filterChoices[fieldKey] = cursor
            ? [...(this.filterChoices[fieldKey] || []), ...results]
            : results
        }
        this.filterCursors[fieldKey] = data.next ?? null
      } catch (e) {
        console.error(`[documentsFilter] fetchFilterChoices [${fieldKey}]:`, e)
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },

    resetTreeFilters() {
      this.filters.types = []
      this.filters.scopes = []
      this.filters.categories = []
      this.filters.status = []
      this.filters.sections = []
      this.filters.authors = []
    },
  },
})
